import { useState, useEffect, useCallback } from 'react';
import { useAuthContext } from '@/contexts/auth-context';
import { onboardingService } from '@/services/onboardingService';
import { OnboardingStatus } from '@/types/onboarding';

interface OnboardingStatusResult {
  isCompleted: boolean | null;
  isLoading: boolean;
  refreshStatus: () => Promise<boolean>;
  markAsCompleted: () => Promise<void>;
}

/**
 * Hook para verificar e gerenciar o status de onboarding do usuário
 * Verifica múltiplas fontes (localStorage e API) para determinar se o onboarding está completo
 * Fornece métodos para atualizar o status
 */
export const useOnboardingStatus = (): OnboardingStatusResult => {
  const { user, loading: authLoading } = useAuthContext();
  const [isCompleted, setIsCompleted] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Verificar o status de onboarding
  const checkOnboardingStatus = useCallback(async (): Promise<boolean> => {
    if (!user?.id) {
      setIsLoading(false);
      return false;
    }

    try {
      // Verificar primeiramente no localStorage para agilidade
      const localStorageCompleted = localStorage.getItem('onboarding_completed') === 'true';
      const localStorageUserId = localStorage.getItem('onboarding_user_id');
      
      // Se temos o status no localStorage e é para o mesmo usuário, usamos esta informação
      if (localStorageCompleted && localStorageUserId === user.id) {
        console.log('useOnboardingStatus: Status encontrado no localStorage - Completo');
        return true;
      }
      
      // Verificação através da API do onboardingService
      const onboardingState = await onboardingService.getOnboardingState(user.id);
      const isCompletedFromService = onboardingState?.status === OnboardingStatus.COMPLETED;
      
      // Verificação adicional através da API dedicada
      const isCompletedFromDirectApi = await onboardingService.isOnboardingComplete(user.id);
      
      // Se qualquer verificação confirmar que está completo, consideramos completo
      const completed = isCompletedFromService || isCompletedFromDirectApi;
      
      // Se o onboarding foi completado, salvar no localStorage para futuras verificações
      if (completed) {
        try {
          localStorage.setItem('onboarding_completed', 'true');
          localStorage.setItem('onboarding_user_id', user.id);
          localStorage.setItem('onboarding_completed_at', new Date().toISOString());
        } catch (e) {
          console.error('useOnboardingStatus: Erro ao salvar status no localStorage', e);
        }
      }
      
      return completed;
    } catch (error) {
      console.error('useOnboardingStatus: Erro ao verificar status de onboarding:', error);
      
      // Em caso de erro, verificar no localStorage como fallback
      const localStorageCompleted = localStorage.getItem('onboarding_completed') === 'true';
      const localStorageUserId = localStorage.getItem('onboarding_user_id');
      
      if (localStorageCompleted && localStorageUserId === user.id) {
        console.log('useOnboardingStatus: Usando status do localStorage após falha na API - Completo');
        return true;
      }
      
      // Se não temos informação, assumimos que não está completo
      return false;
    }
  }, [user?.id]);

  // Atualizar o status
  const refreshStatus = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    try {
      const completed = await checkOnboardingStatus();
      setIsCompleted(completed);
      return completed;
    } finally {
      setIsLoading(false);
    }
  }, [checkOnboardingStatus]);

  // Marcar o onboarding como completo
  const markAsCompleted = useCallback(async (): Promise<void> => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      // Usar a API do onboardingService
      await onboardingService.completeOnboarding(user.id);
      
      // Garantir que o localStorage também está atualizado
      localStorage.setItem('onboarding_completed', 'true');
      localStorage.setItem('onboarding_user_id', user.id);
      localStorage.setItem('onboarding_completed_at', new Date().toISOString());
      
      setIsCompleted(true);
    } catch (error) {
      console.error('useOnboardingStatus: Erro ao marcar onboarding como completo:', error);
      
      // Mesmo com erro na API, marcar como completo no localStorage
      localStorage.setItem('onboarding_completed', 'true');
      localStorage.setItem('onboarding_user_id', user.id);
      localStorage.setItem('onboarding_completed_at', new Date().toISOString());
      
      setIsCompleted(true);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Efeito para verificar o status quando o usuário é carregado
  useEffect(() => {
    if (!authLoading && user?.id) {
      refreshStatus();
    } else if (!authLoading) {
      setIsLoading(false);
    }
  }, [authLoading, user?.id, refreshStatus]);

  return {
    isCompleted,
    isLoading,
    refreshStatus,
    markAsCompleted
  };
}; 